import type { Asset } from '@odo/shared-ui';
import xrayIcon from '../../../../assets/icons/creation/xray.svg';
import cephIcon from '../../../../assets/icons/creation/ceph.svg';
import stlIcon from '../../../../assets/icons/stl.svg';
import type { SubmissionFiles } from './submissionFiles';
import type { SubmissionFileAccess } from './useSubmissionFileAccess';
import { radiographCaption, stlCaption } from './fileCaptions';
import { SubsectionHeading, UploadedFileRow } from './SubmissionStepParts';

interface UploadAreaProps {
    label: string;
    icon: string;
    assets: readonly Asset[];
    access: SubmissionFileAccess;
    captionFor?: (asset: Asset) => string | null;
}

/** Read-only copy of the dashboard's FileUploadCard: dashed drop area + file list. */
function UploadArea({ label, icon, assets, access, captionFor }: UploadAreaProps) {
    const hasFiles = assets.length > 0;
    return (
        <div
            className={`border-2 border-dashed rounded-2xl p-3 min-h-32 flex flex-col gap-2 min-w-0 ${
                hasFiles ? 'border-green-500' : 'border-gray-300'
            }`}
        >
            <div className="flex flex-col items-center text-center">
                <img src={icon} alt="" className="h-10 w-10 mb-1 mt-2" />
                <p className="text-sm leading-6 text-gray-800">{label}</p>
            </div>
            {hasFiles && (
                <div className="flex flex-col gap-2">
                    {assets.map((asset) => (
                        <UploadedFileRow
                            key={String(asset.assetId)}
                            asset={asset}
                            access={access}
                            caption={captionFor?.(asset) ?? null}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

interface MedicalImagesSectionProps {
    files: SubmissionFiles;
    access: SubmissionFileAccess;
    complete: boolean;
}

export function MedicalImagesSection({ files, access, complete }: MedicalImagesSectionProps) {
    return (
        <div className="flex flex-col h-full">
            <SubsectionHeading title="Radiographs" complete={complete} />
            <div className="grid grid-cols-2 gap-4 w-full flex-1">
                <UploadArea
                    label="Radiographs"
                    icon={xrayIcon}
                    assets={files.radiographs}
                    access={access}
                    captionFor={radiographCaption}
                />
                <UploadArea label="CBCT" icon={cephIcon} assets={files.cbct} access={access} />
            </div>
        </div>
    );
}

interface StlsSectionProps {
    files: SubmissionFiles;
    access: SubmissionFileAccess;
    complete: boolean;
}

/** The uploaded STL files (the scan ID sits with the treatment choices). */
export function StlsSection({ files, access, complete }: StlsSectionProps) {
    return (
        <div>
            <SubsectionHeading title="STLs" complete={complete} />
            {files.stls.length > 0 ? (
                <div className="flex flex-col gap-2">
                    {files.stls.map((asset) => (
                        <UploadedFileRow
                            key={String(asset.assetId)}
                            asset={asset}
                            access={access}
                            caption={stlCaption(asset)}
                            icon={stlIcon}
                        />
                    ))}
                </div>
            ) : (
                <p className="text-sm italic text-gray-400">No STL files uploaded</p>
            )}
        </div>
    );
}
